import styles from '../ProductPageLayout/ProductPageLayout.module.css'
import type { PricingQuoteResult } from '~/utils/pricingQuote'
import React, { FC, useState, useCallback, useRef, useMemo } from 'react'
import { useEffect } from 'react'
import { useRouter } from '~/hooks/useRouter'
import { ConfiguratorPageShell } from '~/components/ConfiguratorPageShell/ConfiguratorPageShell'
import { useMediaQuery } from '@mui/material'
import { cdn } from '~/lib/cdn'
import { WardrobePartsProvider } from '~/context/wardrobePartsContext'
import { OpeningType } from '~/components/ThreeDModel/furnitureConfig'
import {
  ProductImageSelect,
  ProductImageSelectComponent,
} from '~/components/ProductPage/productTypeComponents/wardrobe/ProductImageSelect'
import {
  ProductDimensions,
  ProductDimensionsComponent,
} from '~/components/ProductPage/productTypeComponents/ProductDimensions'
import {
  ProductColors,
  ProductColorsComponent,
} from '~/components/ProductPage/productTypeComponents/ProductColors'
import {
  ProductSelect,
  ProductSelectComponent,
} from '~/components/ProductPage/productTypeComponents/ProductSelect'
import {
  ProductFurniture,
  ProductFurnitureComponent,
  ProductFurniturePredefinedValue,
} from '~/components/ProductPage/productTypeComponents/ProductFurniture'
import { ProductSectionPredefinedValue } from '~/components/ProductPage/productTypeComponents/wardrobe/ProductSections'
import {
  ProductPrice,
  ProductPriceComponent,
} from '~/components/ProductPage/productTypeComponents/ProductPrice'
import { ProductGalleryComponent } from '~/components/ProductPage/productTypeComponents/ProductGallery'
import { FormattedMessage } from 'react-intl'
import { useCart } from '~/context/cartContext'
import { Dimension } from '../ProductListPage/products'
import { use3DFurnitureProps } from '~/hooks/use3DFurnitureProps'
import { DEFAULT_WARDROBE } from './productTypes/wardrobe'
import {
  FurnitureViewer,
  FurnitureViewerRef,
} from '../ThreeDModel/FurnitureViewer'
import {
  ProductGalleryColorsConfig,
} from '~/components/ProductPage/productTypeComponents/ProductGalleryColors'
import { useConfiguratorConfigOptional } from '~/context/urlConfigContext'
import { ShareConfigModal } from '~/components/ShareConfigModal/ShareConfigModal'
import {
  ProductWardrobeColumns,
  ProductWardrobeColumnsComponent,
} from '~/components/ProductPage/productTypeComponents/wardrobe/ProductWardrobeColumns'
import type { ButtonOptionsType } from '~/components/ButtonSelect/ButtonSelect'
import { ButtonSelect } from '~/components/ButtonSelect/ButtonSelect'
import {
  NavSection,
  NAV_ORDER,
  SECTION_LABELS,
  isNavSection,
} from '~/components/ProductPage/productTypeComponents/sectionRegistry'
import { getColorItemByName } from '~/utils/colorDictionary'
import { useFurniture } from '~/context/furnitureContext'
import { findChipboardIdByColorName } from '~/utils/furnitureBySku'

export type ProductComponent =
  | ProductImageSelectComponent
  | ProductGalleryComponent
  | ProductDimensionsComponent
  | ProductColorsComponent
  | ProductSelectComponent
  | ProductFurnitureComponent
  | ProductPriceComponent
  | ProductGalleryColorsConfig
  | ProductWardrobeColumnsComponent
export type PredefinedValue = {
  sections?: ProductSectionPredefinedValue
  imageSelect?: string
  imageCarousel?: string[]
  gallery?: string[]
  dimensions?: Dimension
  colors?: string
  galleryColors?: string
  select?: string
  furniture?: ProductFurniturePredefinedValue
  price?: number
  screenshot?: string
  quote?: PricingQuoteResult
}
interface ProductPageProps {
  components: () => ProductComponent[]
  name: string
  values?: PredefinedValue
  furnitureType?: 'wardrobe' | 'wardrobe-new-collection'
}

// Helpers
const filterNavigable = (components: ProductComponent[]) =>
  components.filter((c): c is Extract<ProductComponent, { type: NavSection }> =>
    isNavSection(c.type as string)
  )

export const ProductPage: FC<ProductPageProps> = ({
  components,
  name,
  values,
  furnitureType = 'wardrobe',
}) => {
  const router = useRouter()
  const locale = (router.query.locale as string) || 'ro'
  const { addItem } = useCart()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [activeColumnTab, setActiveColumnTab] = useState(0)
  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | null>(
    null
  )
  const furnitureViewerRef = useRef<FurnitureViewerRef>(null)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)

  // Get current config for share functionality
  const urlConfigCtx = useConfiguratorConfigOptional()

  // Backend furniture definition (chipboards) for resolving the selected color
  // to a backend chipboardId sent with the pricing quote.
  const furniture = useFurniture()

  // Handle tab change from UI to update 3D selection
  const handleTabChange = useCallback((index: number) => {
    setActiveColumnTab(index)
    setSelectedColumnIndex(index)
  }, [])

  // Close doors when handle type is changed (desktop only)
  // On mobile, doors are closed automatically via useEffect when switching to 'furniture' section
  const handleOpeningTypeChange = useCallback(() => {
    if (!isMobile) {
      setSelectedColumnIndex(null)
    }
  }, [isMobile])

  const currentComponents = components()

  // ---------- DESKTOP renderer (unchanged stack) ----------
  const getComponent = (component: ProductComponent): React.ReactNode => {
    switch (component.type) {
      case 'imageSelect':
        return (
          <ProductImageSelect
            configuration={component}
            predefinedValue={values?.imageSelect ?? undefined}
          />
        )
      case 'dimensions':
        return (
          <ProductDimensions
            configuration={component}
            predefinedValue={values?.dimensions ?? undefined}
          />
        )
      case 'colors':
        return (
          <ProductColors
            configuration={component}
            predefinedValue={values?.colors ?? undefined}
          />
        )
      case 'select':
        return (
          <ProductSelect
            configuration={component}
            predefinedValue={values?.select ?? undefined}
          />
        )
      case 'furniture':
        if (furnitureType === 'wardrobe-new-collection') return null
        return (
          <ProductFurniture
            configuration={{ ...component, furnitureType }}
            predefinedValue={values?.furniture ?? undefined}
            onOpeningTypeChange={handleOpeningTypeChange}
          />
        )
      case 'wardrobeColumns':
        return (
          <ProductWardrobeColumns
            configuration={component}
            activeColumnIndex={activeColumnTab}
            onActiveColumnChange={handleTabChange}
          />
        )
      default:
        return null
    }
  }

  // ---------- MOBILE: chips + core-only panel ----------
  // Build available sections (present + valid for current mode)
  const isNewCollection = furnitureType === 'wardrobe-new-collection'
  const navComponents = filterNavigable(currentComponents)
    .filter((c) => !(isNewCollection && c.type === 'furniture'))
    .sort(
      (a, b) =>
        NAV_ORDER.indexOf(a.type as NavSection) -
        NAV_ORDER.indexOf(b.type as NavSection)
    )

  const [activeSection, setActiveSection] = useState<NavSection | null>(
    navComponents[0]?.type ?? null
  )

  // Update activeSection when navComponents changes (e.g., switching between 3D and non-3D)
  useEffect(() => {
    if (navComponents.length > 0) {
      const currentActive = navComponents.find((c) => c.type === activeSection)
      if (!currentActive) {
        setActiveSection(navComponents[0]?.type ?? null)
      }
    } else {
      setActiveSection(null)
    }
  }, [navComponents, activeSection])

  // Automatically select column (open door) when entering wardrobeColumns tab
  useEffect(() => {
    if (activeSection === 'wardrobeColumns') {
      // Always ensure a column is selected when entering this tab
      // If no column is selected, automatically select the first column (index 0)
      // This opens the door and makes the column active
      setSelectedColumnIndex((prevIndex) => {
        if (prevIndex === null) {
          setActiveColumnTab(0)
          return 0
        } else {
          // If a column is already selected, ensure activeColumnTab is in sync
          // This ensures the door stays open when switching to this tab
          setActiveColumnTab(prevIndex)
          return prevIndex
        }
      })
    } else if (activeSection === 'furniture') {
      // When entering furniture tab, close all doors (deselect all columns)
      setSelectedColumnIndex(null)
    }
  }, [activeSection])

  const renderCore = (type: NavSection) => {
    const comp = currentComponents.find((c) => c.type === type)
    if (!comp) return null

    switch (type) {
      case 'dimensions':
        return (
          <ProductDimensions
            configuration={comp as ProductDimensionsComponent}
            predefinedValue={values?.dimensions ?? undefined}
          />
        )
      case 'colors':
        return (
          <ProductColors
            configuration={comp as ProductColorsComponent}
            predefinedValue={values?.colors ?? undefined}
          />
        )
      case 'wardrobeColumns':
        return (
          <ProductWardrobeColumns
            configuration={comp as ProductWardrobeColumnsComponent}
            activeColumnIndex={activeColumnTab}
            onActiveColumnChange={handleTabChange}
          />
        )
      case 'select':
        return (
          <ProductSelect
            configuration={comp as ProductSelectComponent}
            predefinedValue={values?.select ?? undefined}
          />
        )
      case 'furniture': {
        return (
          <ProductFurniture
            configuration={{
              ...(comp as ProductFurnitureComponent),
              furnitureType: 'wardrobe',
            }}
            predefinedValue={values?.furniture ?? undefined}
            onOpeningTypeChange={handleOpeningTypeChange}
          />
        )
      }
      default:
        return null
    }
  }

  const priceComponent = currentComponents.find(
    (component) => component.type === 'price'
  )
  const dimensionsComp = currentComponents.find(
    (c) => c.type === 'dimensions'
  ) as ProductDimensionsComponent | undefined
  const wardrobeColumnsComp = currentComponents.find(
    (c) => c.type === 'wardrobeColumns'
  ) as ProductWardrobeColumnsComponent | undefined
  const furnitureComp = currentComponents.find(
    (c) => c.type === 'furniture'
  ) as ProductFurnitureComponent | undefined

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Extract all 3D props using shared hook (wardrobe uses automatic column layout)
  const furniture3DProps = use3DFurnitureProps(
    currentComponents,
    values,
    DEFAULT_WARDROBE,
    true, // isWardrobe = true (enables automatic column layout)
    furnitureType
  )

  const colorsComp = currentComponents.find((c) => c.type === 'colors') as
    | ProductColorsComponent
    | undefined

  const renderFileURL = useMemo(() => {
    const colorItem = colorsComp?.selectedColor
      ? getColorItemByName(colorsComp.selectedColor)
      : undefined
    return colorItem?.fileURL
  }, [colorsComp])

  const chipboardId = useMemo(
    () => findChipboardIdByColorName(furniture, colorsComp?.selectedColor),
    [furniture, colorsComp?.selectedColor]
  )

  const renderImages = useMemo(() => {
    if (!renderFileURL) return undefined
    const base = cdn(`/${furnitureType}/renders/large`)
    return [1, 2, 3, 4].map((i) => `${base}/${i}/${renderFileURL}.jpg`)
  }, [renderFileURL, furnitureType])

  const renderThumbnail = useMemo(() => {
    if (!renderFileURL) return undefined
    return cdn(`/${furnitureType}/renders/thumbnails/1/${renderFileURL}.jpg`)
  }, [renderFileURL, furnitureType])

  // Handle column click from 3D viewer to update active tab
  const handleColumnClick = useCallback((index: number | null) => {
    if (index !== null) {
      // Column clicked: update both activeTab and selectedColumn
      setActiveColumnTab(index)
      setSelectedColumnIndex(index)
    } else {
      // Background clicked: only deselect column, keep activeTab
      setSelectedColumnIndex(null)
    }
  }, [])

  const heading = (
    <>
      {/* h2, not h1: the prerender-safe canonical h1 for this route is rendered
          by <SeoContentSection> (main.tsx), outside the lazy route boundary. */}
      <h2 className={styles.visuallyHiddenTitle}>
        <FormattedMessage id="meta.header.configurator.wardrobe" />
      </h2>
      <h2 className={styles.title}>
        <FormattedMessage id={name} />
      </h2>
    </>
  )

  const priceSection = priceComponent ? (
    <div className={styles.priceCont}>
      <ProductPrice
        onAddItem={(price, quote) => {
          const screenshot =
            furnitureViewerRef.current?.captureScreenshot() || undefined
          addItem('wardrobe', currentComponents, {
            ...(values ?? {}),
            price,
            screenshot,
            quote,
          })
        }}
        configuration={priceComponent}
        predefinedValue={values?.price ?? undefined}
        productName={name}
        product={furnitureType}
      />
    </div>
  ) : null

  const controls = isMobile && activeSection && navComponents.length > 0 ? (
    <>
      <div className={styles.mobileNavChips}>
        <ButtonSelect<NavSection>
          options={
            navComponents.map((c) => ({
              value: c.type as NavSection,
              label: SECTION_LABELS[c.type as NavSection].id,
            })) as ButtonOptionsType<NavSection>[]
          }
          defaultSelected={activeSection}
          size="medium"
          className={styles.mobileNavChipsRow}
          onChange={(val) => setActiveSection(val as NavSection)}
        />
      </div>

      <div className={styles.mobileCorePanel}>
        {renderCore(activeSection)}
      </div>

      {(() => {
        const colorsComponent = navComponents.find((c) => c.type === 'colors')
        if (colorsComponent && colorsComponent.type !== activeSection) {
          return (
            <div key="colors-hidden" style={{ display: 'none' }}>
              {renderCore('colors')}
            </div>
          )
        }
        return null
      })()}
    </>
  ) : (
    <>
      {currentComponents.map((component, index) => (
        <div key={index + component.type}>{getComponent(component)}</div>
      ))}
    </>
  )

  return (
    <WardrobePartsProvider
      sku={furnitureType}
      chipboardId={chipboardId}
      width={dimensionsComp?.width ?? 120}
      height={dimensionsComp?.height ?? 240}
      depth={dimensionsComp?.depth ?? 60}
      plintHeight={dimensionsComp?.plintHeight ?? 8}
      columns={wardrobeColumnsComp?.selectedColumns ?? 2}
      columnWidths={wardrobeColumnsComp?.columnWidths ?? []}
      openingType={furnitureComp?.openingOption ?? OpeningType.RoundHandle}
      isMirrored={wardrobeColumnsComp?.isMirrored ?? false}
      columnConfigurations={wardrobeColumnsComp?.columnConfigurations}
    >
      <ConfiguratorPageShell
        viewer={
          <FurnitureViewer
            ref={furnitureViewerRef}
            {...furniture3DProps}
            selectedColumnIndex={selectedColumnIndex}
            onColumnClick={handleColumnClick}
            onShare={() => setIsShareModalOpen(true)}
            onExport={() => furnitureViewerRef.current?.exportModel()}
          />
        }
        renderThumbnails={renderThumbnail ? [renderThumbnail] : []}
        heading={heading}
        priceSection={priceSection}
        controls={controls}
        renderImages={renderImages}
        showOrderProgress
        showInfoBar
        modals={
          urlConfigCtx?.config ? (
            <ShareConfigModal
              isOpen={isShareModalOpen}
              onClose={() => setIsShareModalOpen(false)}
              config={urlConfigCtx.config}
              product={furnitureType}
              locale={locale}
            />
          ) : undefined
        }
      />
    </WardrobePartsProvider>
  )
}
